import { useCallback } from 'react';

import { auditService } from '@/api/domains/audit/audit.service';
import { useHybridExport } from '@/hooks/ui/useHybridExport';
import { useTranslation } from '@/i18n';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { PaginatedListSort } from '@/hooks/ui/usePaginatedList';

import type { AuditPendingFilters } from './useAuditList';


export interface UseAuditExportParams {
  searchText?: string;
  filters: AuditPendingFilters;
  sort: PaginatedListSort | undefined;
}

export interface UseAuditExportResult {
  isExporting: boolean;
  onExport: (format: UIExportFormat) => Promise<void>;
}

/**
 * Descarga del registro de auditoría a Excel. Estrategia híbrida sync/async: el backend responde
 * 200 con el archivo bajo el umbral síncrono, o 202 (job en background) cuando lo supera.
 *
 * Delega el branching sync/202, el estado `isExporting` y las notificaciones en `useHybridExport`;
 * aquí solo se arma el payload y se traducen los textos.
 */
export function useAuditExport(
  { searchText, filters, sort }: UseAuditExportParams,
): UseAuditExportResult {
  const { t: translate } = useTranslation();

  const requestExport = useCallback(
    () => auditService.exportRecords({
      searchText: searchText || undefined,
      dateFrom: filters.startDate,
      dateTo: filters.endDate,
      objectName: filters.entityId,
      program: filters.programId,
      action: filters.actionId,
      user: filters.userId,
      orderBy: sort?.key ?? null,
      orderDirection: sort?.direction ?? null,
    }),
    [searchText, filters, sort],
  );

  const { isExporting, runExport } = useHybridExport({
    requestExport,
    messages: {
      error: translate('audit:messages.exportError'),
      success: translate('audit:messages.exportSuccess'),
      queued: translate('audit:messages.exportQueued'),
    },
  });

  return { isExporting, onExport: runExport };
}
